// API Response Types

export interface Overview {
  timestamp: string;
  bettingEnabled: boolean;
  metrics: {
    activeSignals: number;
    activeOpportunities: number;
    openPositions: number;
    todayPnL: number;
    todayBets: number;
    totalEvents: number;
    matchedEvents: number;
  };
  trends: {
    signalsChange: number;
  };
  lastUpdates: {
    odds: string | null;
    polymarket: string | null;
  };
}

export interface Opportunity {
  id: string;
  polymarketEventId: string;
  tokenId: string;
  detectedAt: string;
  impliedProbOdds: number;
  impliedProbPolymarket: number;
  rawEdge: number;
  adjustedEdge: number;
  recommendedAction: 'buy_pm' | 'sell_pm';
  status: string;
  rlmDirection: string;
  rlmStrength: number;
  signalType: string;
  marketType: string;
  eventId: string;
  event: {
    homeTeam: string;
    awayTeam: string;
    sport: string;
    commenceTime: string;
  };
  polymarketTitle: string;
}

export interface Signal {
  id: string;
  eventId: string;
  detectedAt: string;
  marketType: string;
  signalType: 'SHARP_VS_SQUARE' | 'STEAM_MOVE' | 'CONSENSUS_DIVERGENCE' | 'OPENING_VS_CURRENT' | 'ARBITRAGE' | 'CLV_POSITIVE' | 'VOLUME_SPIKE';
  strength: number;
  direction: string;
  confidence: number;
  details: Record<string, unknown> | null;
  expiresAt: string | null;
  isActive?: boolean;
  event: {
    homeTeam: string;
    awayTeam: string;
    sport: string;
    commenceTime: string;
  };
}

export interface Bet {
  id: string;
  opportunityId: string;
  polymarketEventId: string;
  placedAt: string;
  side: 'BUY' | 'SELL';
  tokenId: string;
  entryPrice: number;
  size: number;
  orderId: string;
  status: 'pending' | 'filled' | 'partial' | 'cancelled';
  resolvedAt: string | null;
  filledPrice: number | null;
  filledAmount: number | null;
  pnl: number | null;
  polymarketTitle: string;
  edge: number;
}

export interface BetStats {
  summary: {
    totalBets: number;
    winningBets: number;
    losingBets: number;
    pendingBets: number;
    winRate: number;
    totalPnL: number;
    totalWagered: number;
    roi: number;
    avgWin: number;
    avgLoss: number;
    largestWin: number;
    largestLoss: number;
  };
  bySport: Array<{ sport: string; bets: number; pnl: number }>;
  bySignalType: Array<{ signalType: string; bets: number; pnl: number }>;
}

export interface DailyPnL {
  days: number;
  data: Array<{
    date: string;
    bets: number;
    wagered: number;
    pnl: number;
  }>;
}

export interface CumulativePnL {
  data: Array<{
    date: string;
    dailyPnL: number;
    cumulativePnL: number;
  }>;
}

export interface RiskStatus {
  status: 'ok' | 'warning' | 'critical';
  alerts: string[];
  dailyPnL: {
    current: number;
    limit: number;
    percentage: number;
  };
  positions: {
    open: number;
    max: number;
    percentage: number;
  };
  cooldown: {
    active: boolean;
    remainingSeconds: number;
    totalSeconds: number;
  };
  limits: {
    maxBetSize: number;
    maxDailyLoss: number;
    maxConcurrentBets: number;
    cooldownSeconds: number;
  };
  lastBetTime: string | null;
  dailyBets: number;
}

export interface Config {
  betting: {
    enabled: boolean;
    maxBetSize: number;
    maxDailyLoss: number;
    maxConcurrentBets: number;
    minEdgeThreshold: number;
    kellyFraction: number;
    cooldownSeconds: number;
  };
  polling: {
    cronSchedule: string;
    oddsApiConfigured: boolean;
    polymarketConfigured: boolean;
  };
  version: string;
}

export interface Event {
  id: string;
  sport: string;
  homeTeam: string;
  awayTeam: string;
  commenceTime: string;
  polymarket: {
    eventId: string;
    title: string;
    matchingConfidence: number;
    matchMethod: 'exact' | 'normalized' | 'fuzzy' | 'individual_sport' | null;
    betType: 'moneyline' | 'spread' | 'total' | 'player_points' | null;
  } | null;
  signalCount: number;
  opportunityCount: number;
}

export interface EventDetail {
  event: {
    id: string;
    sport: string;
    homeTeam: string;
    awayTeam: string;
    commenceTime: string;
    status: string;
    isLive: boolean;
  };
  polymarket: {
    eventId: string;
    title: string;
    description: string | null;
    matchingConfidence: number;
    matchMethod: 'exact' | 'normalized' | 'fuzzy' | 'individual_sport' | null;
    betType: 'moneyline' | 'spread' | 'total' | 'player_points' | null;
    currentPrice: number | null;
    priceHistory: Array<{
      timestamp: string;
      outcome: string;
      midPrice: number;
      bidPrice: number;
      askPrice: number;
    }>;
  } | null;
  signals: Signal[];
  opportunities: Opportunity[];
  oddsHistory: Array<{
    bookmaker: string;
    timestamp: string;
    outcomeName: string;
    price: number;
    isSharp: boolean;
  }>;
  marketStats: Array<{
    marketType: string;
    bookmakerCount: number;
    lineCount: number;
    minPoint: number | null;
    maxPoint: number | null;
  }>;
  currentOddsByMarket: Record<string, Array<{
    outcomeName: string;
    point: number | null;
    price: number;
    bookmaker: string;
    isSharp: boolean;
    timestamp: string;
  }>>;
  crossMarketStats: {
    pmVsOdds: { divergence: number; implication: string } | null;
    spreadVsMl: { divergence: number; implication: string } | null;
    totalsCorrelation: { divergence: number; implication: string } | null;
  } | null;
  bets: Bet[];
}

// Signal type colors
export const SIGNAL_TYPE_COLORS: Record<string, { bg: string; text: string; emoji: string }> = {
  SHARP_VS_SQUARE: { bg: 'bg-red-500/20', text: 'text-red-400', emoji: '🔴' },
  STEAM_MOVE: { bg: 'bg-blue-500/20', text: 'text-blue-400', emoji: '🔵' },
  CONSENSUS_DIVERGENCE: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', emoji: '🟡' },
  OPENING_VS_CURRENT: { bg: 'bg-green-500/20', text: 'text-green-400', emoji: '🟢' },
  ARBITRAGE: { bg: 'bg-purple-500/20', text: 'text-purple-400', emoji: '💰' },
  CLV_POSITIVE: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', emoji: '📈' },
  VOLUME_SPIKE: { bg: 'bg-orange-500/20', text: 'text-orange-400', emoji: '📊' },
};

// Game summary with grouped signals
export interface GameSummary {
  eventId: string;
  homeTeam: string;
  awayTeam: string;
  sport: string;
  commenceTime: string;
  status: string;
  isLive: boolean;
  signalCount: number;
  signals: Array<{
    id: string;
    eventId: string;
    detectedAt: string;
    marketType: string;
    signalType: string;
    strength: number;
    direction: string;
    confidence: number;
    details: Record<string, unknown> | null;
    expiresAt: string | null;
  }>;
  aggregateStrength: number;
  dominantDirection: 'home' | 'away' | 'over' | 'under' | 'mixed';
  signalTypeBreakdown: Record<string, number>;
  polymarket: {
    eventId: string;
    title: string;
    matchingConfidence: number;
    matchMethod: 'exact' | 'normalized' | 'fuzzy' | 'individual_sport' | null;
    betType: 'moneyline' | 'spread' | 'total' | 'player_points' | null;
  } | null;
  opportunityCount: number;
}

export interface GamesResponse {
  games: GameSummary[];
  sports: string[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}
